<template>
  <div class="message-page">
    <header class="page-hero">
      <div>
        <span class="eyebrow">PUBLIC MESSAGE</span>
        <h2>公共消息管理</h2>
        <p>统一发布全站公告与消息通知；关闭公告后，Web 端将不再弹出和展示。</p>
      </div>
      <el-button type="primary" class="publish-btn" @click="openCreate">
        <el-icon><Plus /></el-icon>发布消息
      </el-button>
    </header>

    <section class="content-panel">
      <div class="toolbar">
        <div class="filters">
          <el-input v-model="filters.keyword" clearable placeholder="搜索标题或内容" @keyup.enter="reload">
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          <el-select v-model="filters.type" placeholder="消息类型" clearable @change="reload">
            <el-option label="公告" value="announcement" />
            <el-option label="消息通知" value="notification" />
          </el-select>
          <el-select v-model="filters.status" placeholder="发布状态" clearable @change="reload">
            <el-option label="已开启" :value="1" />
            <el-option label="已关闭" :value="0" />
          </el-select>
          <el-button type="primary" @click="reload">查询</el-button>
          <el-button @click="resetFilters">重置</el-button>
        </div>
        <el-button :icon="Refresh" :loading="loading" @click="loadMessages">刷新</el-button>
      </div>

      <el-table v-loading="loading" :data="rows" row-key="id" class="message-table">
        <el-table-column label="类型" width="110">
          <template #default="{ row }">
            <el-tag :type="row.message_type === 'announcement' ? 'warning' : 'primary'" effect="dark">
              {{ row.message_type === 'announcement' ? '公告' : '消息通知' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="190" show-overflow-tooltip />
        <el-table-column label="图片" width="90">
          <template #default="{ row }">
            <el-image v-if="row.image_url" class="table-image" :src="resolveImageUrl(row.image_url)" fit="cover"
              :preview-src-list="[resolveImageUrl(row.image_url)]" preview-teleported />
            <span v-else class="no-image">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="content" label="内容" min-width="340" show-overflow-tooltip />
        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-switch :model-value="Number(row.status) === 1" inline-prompt active-text="开" inactive-text="关"
              :loading="row.statusLoading" @change="value => changeStatus(row, value)" />
          </template>
        </el-table-column>
        <el-table-column label="发布时间" width="180">
          <template #default="{ row }">{{ formatTime(row.publish_time) }}</template>
        </el-table-column>
        <el-table-column label="更新时间" width="180">
          <template #default="{ row }">{{ formatTime(row.updated_time) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-button link type="danger" @click="removeMessage(row)">删除</el-button>
          </template>
        </el-table-column>
        <template #empty><el-empty description="暂无公共消息" /></template>
      </el-table>

      <div class="pager-row">
        <el-pagination v-model:current-page="page" v-model:page-size="pageSize" :total="total"
          layout="total, sizes, prev, pager, next, jumper" :page-sizes="[10, 20, 50]"
          @current-change="loadMessages" @size-change="handleSizeChange" />
      </div>
    </section>

    <el-dialog v-model="dialogVisible" :title="form.id ? '编辑公共消息' : '发布公共消息'" width="660px"
      destroy-on-close class="message-dialog">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item label="消息类型" prop="message_type">
          <el-radio-group v-model="form.message_type">
            <el-radio-button label="announcement">公告（弹窗展示）</el-radio-button>
            <el-radio-button label="notification">消息通知（铃铛展示）</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" maxlength="100" show-word-limit placeholder="请输入标题" />
        </el-form-item>
        <el-form-item label="正文内容" prop="content">
          <el-input v-model="form.content" type="textarea" :rows="8" maxlength="5000" show-word-limit resize="none"
            placeholder="请输入公告或通知正文" />
        </el-form-item>
        <el-form-item v-if="form.message_type === 'announcement'" label="公告图片（选填）">
          <el-upload class="announcement-uploader" action="#" :auto-upload="false" :show-file-list="false"
            accept="image/jpeg,image/png,image/webp" :on-change="handleImageChange">
            <div v-if="imagePreview" class="image-preview">
              <img :src="imagePreview" alt="公告图片预览" />
              <div class="image-mask"><span>点击更换图片</span></div>
              <button type="button" class="remove-image" @click.stop="removeImage">移除</button>
            </div>
            <div v-else class="upload-placeholder">
              <el-icon><Plus /></el-icon>
              <div><b>上传公告图片</b><span>JPG / PNG / WEBP，最大 10MB</span></div>
            </div>
          </el-upload>
        </el-form-item>
        <div class="form-grid">
          <el-form-item label="发布时间" prop="publish_time">
            <el-date-picker v-model="form.publish_time" type="datetime" value-format="YYYY-MM-DD HH:mm:ss"
              placeholder="选择发布时间" style="width: 100%" />
          </el-form-item>
          <el-form-item label="立即开启">
            <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="开启" inactive-text="关闭" />
          </el-form-item>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">{{ form.id ? '保存修改' : '确认发布' }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import { API_URL } from '@/util/urls'

const loading = ref(false)
const saving = ref(false)
const dialogVisible = ref(false)
const formRef = ref()
const rows = ref([])
const page = ref(1)
const pageSize = ref(10)
const total = ref(0)
const filters = reactive({ keyword: '', type: '', status: '' })
const imagePreview = ref('')
const selectedImageFile = ref(null)

const emptyForm = () => ({
  id: null,
  message_type: 'announcement',
  title: '',
  content: '',
  image_url: '',
  publish_time: formatForPicker(new Date()),
  status: 1
})
const form = reactive(emptyForm())
const rules = {
  message_type: [{ required: true, message: '请选择消息类型', trigger: 'change' }],
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  content: [{ required: true, message: '请输入正文内容', trigger: 'blur' }],
  publish_time: [{ required: true, message: '请选择发布时间', trigger: 'change' }]
}

function formatForPicker(value) {
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function formatTime(value) {
  if (!value) return '-'
  return formatForPicker(value)
}

function resolveImageUrl(value) {
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  return `${API_URL}/${String(value).replace(/^\/+/, '')}`
}

async function loadMessages() {
  loading.value = true
  try {
    const { data } = await axios.get('/admin/public-messages', {
      params: { page: page.value, pageSize: pageSize.value, ...filters, _t: Date.now() }
    })
    if (data.msg !== 'success') throw new Error(data.error || '获取消息失败')
    rows.value = (data.data?.list || []).map(item => ({ ...item, statusLoading: false }))
    total.value = Number(data.data?.total || 0)
  } catch (error) {
    ElMessage.error(error.response?.data?.error || error.message || '获取消息失败')
  } finally {
    loading.value = false
  }
}

function reload() {
  page.value = 1
  loadMessages()
}

function resetFilters() {
  Object.assign(filters, { keyword: '', type: '', status: '' })
  reload()
}

function handleSizeChange() {
  page.value = 1
  loadMessages()
}

function openCreate() {
  Object.assign(form, emptyForm())
  imagePreview.value = ''
  selectedImageFile.value = null
  dialogVisible.value = true
}

function openEdit(row) {
  Object.assign(form, {
    id: row.id,
    message_type: row.message_type,
    title: row.title,
    content: row.content,
    image_url: row.image_url || '',
    publish_time: formatTime(row.publish_time),
    status: Number(row.status)
  })
  imagePreview.value = resolveImageUrl(row.image_url)
  selectedImageFile.value = null
  dialogVisible.value = true
}

function handleImageChange(uploadFile) {
  const file = uploadFile?.raw
  if (!file) return
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    ElMessage.error('仅支持 JPG、PNG、WEBP 图片')
    return
  }
  if (file.size > 10 * 1024 * 1024) {
    ElMessage.error('公告图片不能超过 10MB')
    return
  }
  selectedImageFile.value = file
  imagePreview.value = URL.createObjectURL(file)
}

function removeImage() {
  selectedImageFile.value = null
  imagePreview.value = ''
  form.image_url = ''
}

async function submit() {
  try {
    await formRef.value.validate()
    saving.value = true
    if (form.message_type === 'announcement' && selectedImageFile.value) {
      const uploadData = new FormData()
      uploadData.append('file', selectedImageFile.value)
      const uploadResponse = await axios.post('/admin/public-messages/upload-image', uploadData)
      if (uploadResponse.data?.msg !== 'success' || !uploadResponse.data?.data?.image_url) {
        throw new Error(uploadResponse.data?.error || '上传公告图片失败')
      }
      form.image_url = uploadResponse.data.data.image_url
    }
    if (form.message_type !== 'announcement') form.image_url = ''
    const url = form.id ? `/admin/public-messages/${form.id}` : '/admin/public-messages'
    const method = form.id ? 'put' : 'post'
    const { data } = await axios[method](url, { ...form })
    if (data.msg !== 'success') throw new Error(data.error || '保存失败')
    ElMessage.success(form.id ? '修改成功' : '发布成功')
    dialogVisible.value = false
    loadMessages()
  } catch (error) {
    if (error === false || (error && typeof error === 'object' && !error.response && !error.message)) return
    ElMessage.error(error.response?.data?.error || error.message || '保存失败')
  } finally {
    saving.value = false
  }
}

async function changeStatus(row, enabled) {
  row.statusLoading = true
  try {
    const { data } = await axios.put(`/admin/public-messages/${row.id}/status`, { status: enabled ? 1 : 0 })
    if (data.msg !== 'success') throw new Error(data.error || '状态修改失败')
    row.status = enabled ? 1 : 0
    ElMessage.success(enabled ? '消息已开启' : '消息已关闭')
  } catch (error) {
    ElMessage.error(error.response?.data?.error || error.message || '状态修改失败')
  } finally {
    row.statusLoading = false
  }
}

async function removeMessage(row) {
  try {
    await ElMessageBox.confirm(`确定删除“${row.title}”吗？删除后不可恢复。`, '删除公共消息', {
      type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消'
    })
    const { data } = await axios.delete(`/admin/public-messages/${row.id}`)
    if (data.msg !== 'success') throw new Error(data.error || '删除失败')
    ElMessage.success('删除成功')
    if (rows.value.length === 1 && page.value > 1) page.value--
    loadMessages()
  } catch (error) {
    if (error === 'cancel' || error === 'close') return
    ElMessage.error(error.response?.data?.error || error.message || '删除失败')
  }
}

onMounted(loadMessages)
</script>

<style scoped>
.message-page { min-height: 100%; padding: 24px; box-sizing: border-box; color: #e8eef9; background: #08101f; }
.page-hero { display: flex; justify-content: space-between; align-items: center; padding: 26px 30px; border: 1px solid #23344e; border-radius: 14px; background: linear-gradient(120deg, #111d31, #142044 72%, #1c2960); }
.eyebrow { color: #4aa3ff; font-size: 12px; font-weight: 800; letter-spacing: .16em; }
.page-hero h2 { margin: 8px 0; font-size: 25px; color: #fff; }
.page-hero p { margin: 0; color: #91a2bd; }
.publish-btn { height: 42px; padding: 0 20px; }
.content-panel { margin-top: 16px; padding: 22px; border: 1px solid #223149; border-radius: 14px; background: #0e1726; }
.toolbar { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.filters { display: flex; gap: 10px; flex-wrap: wrap; }
.filters .el-input { width: 260px; }
.filters .el-select { width: 150px; }
.message-table { --el-table-bg-color: transparent; --el-table-tr-bg-color: transparent; --el-table-header-bg-color: #18263c; --el-table-row-hover-bg-color: #17243a; --el-table-border-color: #25364f; --el-table-text-color: #cdd8ea; --el-table-header-text-color: #a9bad2; }
.pager-row { display: flex; justify-content: flex-end; padding-top: 22px; }
.form-grid { display: grid; grid-template-columns: 1fr 160px; gap: 18px; }
.table-image { width: 48px; height: 36px; border-radius: 6px; }
.no-image { color: #607089; }
.announcement-uploader { width: 100%; }
.announcement-uploader :deep(.el-upload) { width: 100%; }
.upload-placeholder { width: 100%; min-height: 112px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; gap: 14px; border: 1px dashed #3a4e70; border-radius: 10px; color: #7f91ad; background: #111b2a; transition: .2s; }
.upload-placeholder:hover { border-color: #409eff; color: #70b5ff; background: #142238; }
.upload-placeholder .el-icon { font-size: 28px; }
.upload-placeholder b, .upload-placeholder span { display: block; }
.upload-placeholder b { margin-bottom: 6px; color: #dce7f7; }
.upload-placeholder span { font-size: 12px; }
.image-preview { position: relative; width: 100%; max-height: 300px; overflow: hidden; border: 1px solid #2d405e; border-radius: 10px; background: #0b1320; }
.image-preview img { display: block; width: 100%; max-height: 300px; object-fit: contain; }
.image-mask { position: absolute; inset: 0; display: grid; place-items: center; color: transparent; background: transparent; transition: .2s; }
.image-preview:hover .image-mask { color: #fff; background: rgba(3, 8, 18, .52); }
.remove-image { position: absolute; top: 10px; right: 10px; padding: 6px 10px; border: 1px solid rgba(255, 100, 110, .48); border-radius: 6px; color: #ff7b85; background: rgba(15, 20, 30, .88); cursor: pointer; }

/* 页面筛选、弹窗表单、分页器统一使用深色控件。 */
.message-page :deep(.el-input__wrapper),
.message-page :deep(.el-select .el-input__wrapper),
.message-page :deep(.el-select__wrapper) { background: #121c2b !important; box-shadow: 0 0 0 1px #2b3b55 inset !important; }
.message-page :deep(.el-input__inner),
.message-page :deep(.el-select__placeholder),
.message-page :deep(.el-select__selected-item) { color: #dbe6f6 !important; }
.message-page :deep(.el-button:not(.el-button--primary):not(.is-link)) { color: #c8d4e7; border-color: #30415e; background: #161f2d; }
.pager-row :deep(.btn-prev), .pager-row :deep(.btn-next), .pager-row :deep(.el-pager li),
.pager-row :deep(.el-pagination__jump .el-input__wrapper), .pager-row :deep(.el-pagination__sizes .el-input__wrapper),
.pager-row :deep(.el-pagination__sizes .el-select__wrapper) { color: #afbdd2; border-color: #2c3c56; background: #161f2d !important; box-shadow: 0 0 0 1px #2c3c56 inset !important; }
.pager-row :deep(.el-pager li.is-active) { color: #fff; background: #409eff !important; }

:global(.message-dialog) { --el-bg-color: #0f151d; --el-bg-color-overlay: #0f151d; --el-text-color-primary: #e8eef9; --el-text-color-regular: #b4c0d3; --el-border-color: #2a374a; background: #0f151d !important; }
:global(.message-dialog .el-dialog__header) { padding-bottom: 18px; border-bottom: 1px solid #293648; }
:global(.message-dialog .el-dialog__title) { color: #f2f6fc; font-weight: 700; }
:global(.message-dialog .el-dialog__footer) { padding-top: 18px; border-top: 1px solid #293648; }
:global(.message-dialog .el-form-item__label) { color: #9cacbf; }
:global(.message-dialog .el-input__wrapper),
:global(.message-dialog .el-textarea__inner),
:global(.message-dialog .el-select__wrapper) { color: #dce6f4 !important; background: #121c28 !important; box-shadow: 0 0 0 1px #30415a inset !important; }
:global(.message-dialog .el-input__inner), :global(.message-dialog .el-textarea__inner) { color: #dce6f4 !important; }
:global(.message-dialog .el-radio-button__inner) { color: #b8c4d6; border-color: #30415a; background: #121c28; box-shadow: none !important; }
:global(.message-dialog .el-radio-button__original-radio:checked + .el-radio-button__inner) { color: #fff; border-color: #409eff; background: #409eff; }
:global(.message-dialog .el-button:not(.el-button--primary)) { color: #c8d4e7; border-color: #30415e; background: #161f2d; }
@media (max-width: 900px) { .page-hero, .toolbar { align-items: flex-start; flex-direction: column; } .filters .el-input, .filters .el-select { width: 100%; } .form-grid { grid-template-columns: 1fr; } }
</style>
